import { useCallback, useEffect, useRef } from 'react';
import { useAuth } from '@clerk/clerk-react';
import { datafluxRum } from '@cloudcare/browser-rum';
import axios, { type AxiosResponse } from 'axios';
import axiosInstance from '../apis/axiosInstance';

export interface TimApiRequest {
  type: 'API_REQUEST';
  protocolVersion: 1;
  requestId: string;
  method: string;
  path: string;
  body?: unknown;
  headers?: Record<string, string>;
}
export interface TimApiResponse {
  type: 'API_RESPONSE';
  protocolVersion: 1;
  requestId: string;
  status: number;
  body: unknown;
  serverDate?: string | null;
}

// ── TIM_SET_STATE: 宿主 → iframe ──────────────────────────────────────
export type SetStatePayload =
  | {
      type: 'SESSION';
      protocolVersion: 1;
      userId?: string;
      role: 'brand' | 'creator';
      platform: 'pc' | 'h5';
      runtime?: 'web' | 'app';
      lang: string;
    }
  | TimApiResponse
  | { type: 'OPEN'; orderId?: string; campaignId?: string; view?: 'list' }
  | { type: 'LANG'; lang: string }
  | { type: 'IM_VISIBLE'; visible: boolean }
  | {
      type: 'DOWNLOAD_RESULT';
      requestId: string;
      ok: boolean;
      canceled?: boolean;
      error?: string;
    }
  | {
      type: 'DOWNLOAD_PROGRESS';
      requestId: string;
      progress?: number;
      loadedBytes?: number;
      totalBytes?: number;
      stage?: 'started' | 'downloading' | 'saving';
    };

export interface SetStateMessage {
  type: 'TIM_SET_STATE';
  payload: SetStatePayload;
}

// ── TIM_DISPATCH: iframe → 宿主 ───────────────────────────────────────
export interface TimUnreadDetails {
  scope: 'order_groups';
  items: Array<{
    orderId: string;
    campaignId?: string;
    timGroupId: string;
    conversationId: string;
    count: number;
  }>;
}

/** Latest message per genuinely unread order conversation; never includes marker-only items. */
export interface TimUnreadMessageItem {
  conversationId: string;
  timGroupId: string;
  orderId: string;
  campaignId?: string;
  campaignName: string;
  unreadCount: number;
  peer: { id: string; name: string; avatarUrl?: string };
  message: {
    id?: string;
    sequence?: number;
    sentAt: number; // Unix milliseconds
    preview: string; // Plain text, localized by im-ui
    senderId: string;
    senderName?: string;
    senderAvatarUrl?: string;
    isFromSelf: boolean;
  };
}

export interface TimUnreadMessages {
  userId: string;
  items: TimUnreadMessageItem[];
}

export type DispatchPayload =
  | { type: 'READY' }
  | TimApiRequest
  | { type: 'API_CANCEL'; requestId: string }
  | { type: 'CLIENT_READY'; ready: boolean }
  | { type: 'UNREAD_MESSAGES'; snapshot: TimUnreadMessages }
  | { type: 'UNREAD_COUNT'; count: number; details?: TimUnreadDetails }
  | { type: 'CLOSE' }
  | {
      type: 'OPEN_ORDER';
      orderId: string;
      campaignId?: string;
      timGroupId?: string;
      conversationId?: string;
      targetWindowName?: string;
    }
  | {
      type: 'DOWNLOAD';
      requestId?: string;
      url: string;
      fileName: string;
      mimeType?: string;
      mediaKind?: 'image' | 'video' | 'file';
      saveToPhotos?: boolean;
    };

export interface DispatchMessage {
  type: 'TIM_DISPATCH';
  payload: DispatchPayload;
}

export interface TimPresence {
  pageSessionId: string;
  seq: number;
  pageVisible: boolean;
  toastCapable: boolean;
  imVisible: boolean;
}

export interface TimToastRender5sAck {
  pageSessionId: string;
  displayId: string;
  shownAt: number;
  pageVisible: true;
  items: Array<{ timGroupId: string; msgSeq: number }>;
}

type TimHostRequest = Pick<
  TimApiRequest,
  'requestId' | 'method' | 'path' | 'body' | 'headers'
>;
type TimApiResult = Pick<TimApiResponse, 'status' | 'body' | 'serverDate'>;
const errorResult = (status: number, message: string): TimApiResult => ({
  status,
  body: { message }
});

const newId = () =>
  Array.from(crypto.getRandomValues(new Uint8Array(16)), (n) =>
    n.toString(16).padStart(2, '0')
  ).join('');
type Options = {
  role: 'brand' | 'creator';
  platform: 'pc' | 'h5';
  runtime: 'web' | 'app';
  lang: string;
  iframeReady: boolean;
  imVisible: boolean;
  send: (payload: SetStatePayload) => void;
};

// React lifecycle wiring for this bridge; HTTP always uses the host's existing client.
export function useTimBridge(options: Options) {
  const { userId, orgId } = useAuth();
  const mounted = useRef(true);
  const pending = useRef(new Map<string, AbortController>());
  const scope = useRef({
    pageId: newId(),
    seq: 0,
    clientReady: false,
    hidden: false
  });
  const available = !!userId && (options.role !== 'brand' || !!orgId);
  const current = useRef({ ...options, userId, available });
  current.current = { ...options, userId, available };
  const dispose = useCallback(() => {
    pending.current.forEach((controller) => controller.abort());
    pending.current.clear();
  }, []);
  useEffect(() => {
    mounted.current = true;
    return () => {
      mounted.current = false;
      dispose();
    };
  }, [dispose]);
  const requestHttp = useCallback(
    async (
      input: TimHostRequest,
      source: 'im' | 'host' = 'im'
    ): Promise<TimApiResult> => {
      const getPresence = () => ({
        platform: current.current.platform,
        runtime: current.current.runtime,
        pageSessionId: scope.current.pageId,
        seq: ++scope.current.seq,
        pageVisible:
          !scope.current.hidden && document.visibilityState === 'visible',
        toastCapable: !scope.current.hidden && scope.current.clientReady,
        imVisible: current.current.imVisible
      });
      const canSendToastAck = () =>
        mounted.current &&
        !scope.current.hidden &&
        scope.current.clientReady &&
        document.visibilityState === 'visible';
      if (
        typeof input.requestId !== 'string' ||
        !/^[a-zA-Z0-9_-]{1,100}$/.test(input.requestId) ||
        typeof input.path !== 'string'
      )
        return errorResult(400, 'Invalid TIM API request');
      const context = current.current;
      const isCurrent = () =>
        mounted.current && current.current.userId === context.userId;
      if (!context.available || !isCurrent())
        return errorResult(409, 'Host session changed');
      if (pending.current.size >= 50 || pending.current.has(input.requestId))
        return errorResult(429, 'Too many pending IM requests');
      // The iframe may call any TIM route; the host owns origin, credentials and identity.
      let url: URL;
      try {
        url = new URL(input.path, 'https://tim.invalid');
      } catch {
        return errorResult(400, 'Invalid TIM API path');
      }
      if (
        !(
          input.path === '/api/tim' ||
          input.path.startsWith('/api/tim/') ||
          input.path.startsWith('/api/tim?')
        ) ||
        url.origin !== 'https://tim.invalid' ||
        !(url.pathname === '/api/tim' || url.pathname.startsWith('/api/tim/'))
      )
        return errorResult(403, 'TIM API operation not allowed');
      // Preserve caller payloads, including arrays, primitives and null. Presence
      // heartbeats may omit host-only context; fill defaults without overwriting input.
      const body =
        url.pathname === '/api/tim/presence' &&
        (input.body === undefined ||
          (input.body !== null &&
            typeof input.body === 'object' &&
            !Array.isArray(input.body)))
          ? {
              ...getPresence(),
              ...(input.body as Record<string, unknown> | undefined)
            }
          : input.body;
      // Supply the session role only when the caller did not provide a query role.
      // Body role, when present, is interpreted and authorized by the backend.
      if (!url.searchParams.has('role'))
        url.searchParams.set('role', context.role);
      const controller = new AbortController();
      pending.current.set(input.requestId, controller);
      const timeout = setTimeout(
        () => controller.abort(),
        url.pathname === '/api/tim/toast-render-5s-ack' ? 5000 : 20_000
      );
      const startedAt = Date.now();
      let status = 0;
      let captured: AxiosResponse | undefined;
      let blocked = false;
      let abortWait: (() => void) | undefined;
      try {
        const adapter = axios.getAdapter(
          axiosInstance.defaults.adapter || axios.defaults.adapter
        );
        const task = axiosInstance.request({
          url: url.pathname + url.search,
          method: input.method,
          data: body,
          headers: input.headers,
          signal: controller.signal,
          timeout:
            url.pathname === '/api/tim/toast-render-5s-ack' ? 5000 : 20_000,
          // Keep the host request/response interceptors intact. Check scope and ACK
          // visibility after async authentication, immediately before its transport.
          adapter: async (config) => {
            if (
              !isCurrent() ||
              controller.signal.aborted ||
              (source === 'host' &&
                url.pathname === '/api/tim/toast-render-5s-ack' &&
                !canSendToastAck())
            ) {
              blocked = true;
              throw new axios.CanceledError('TIM request no longer valid');
            }
            try {
              const response = await adapter(config);
              return response;
            } catch (error) {
              // Host interceptors may reduce AxiosError to { message }. Preserve
              // only HTTP response data for the RPC; never serialize the config/token.
              if (axios.isAxiosError(error)) captured = error.response;
              throw error;
            }
          }
        });
        const response = await Promise.race([
          task,
          new Promise<never>((_resolve, reject) => {
            abortWait = () =>
              reject(new axios.CanceledError('TIM request cancelled'));
            controller.signal.addEventListener('abort', abortWait, {
              once: true
            });
          })
        ]);
        if (!isCurrent())
          return errorResult((status = 409), 'Host session changed');
        status = response.status;
        return {
          status,
          body: response.data,
          serverDate: response.headers?.date || null
        };
      } catch {
        if (!isCurrent() || blocked)
          return errorResult(
            (status = 409),
            'Host session changed or request no longer valid'
          );
        if (controller.signal.aborted)
          return errorResult(
            (status = 408),
            'TIM API request cancelled or timed out'
          );
        if (captured) {
          status = captured.status;
          let body = captured.data;
          if (typeof body === 'string') {
            try {
              body = JSON.parse(body);
            } catch {
              body = { message: `TIM API returned HTTP ${status}` };
            }
          }
          return { status, body, serverDate: captured.headers?.date || null };
        }
        return errorResult((status = 503), 'Host API request unavailable');
      } finally {
        clearTimeout(timeout);
        if (abortWait)
          controller.signal.removeEventListener('abort', abortWait);
        pending.current.delete(input.requestId);
        try {
          datafluxRum.addAction('tim_api_request', {
            requestId: input.requestId,
            path: url.pathname,
            method: input.method,
            status,
            current: isCurrent(),
            elapsedMs: Date.now() - startedAt
          });
        } catch {
          /* Diagnostic delivery cannot break requests. */
        }
      }
    },
    []
  );
  const request = useCallback(
    async (input: TimApiRequest) => {
      const user = current.current.userId;
      const result =
        input.protocolVersion === 1
          ? await requestHttp(input)
          : errorResult(400, 'Unsupported TIM bridge protocol version');
      if (mounted.current && current.current.userId === user) {
        current.current.send({
          type: 'API_RESPONSE',
          protocolVersion: 1,
          requestId: input.requestId,
          ...result
        });
      }
      return result;
    },
    [requestHttp]
  );
  const cancel = useCallback((requestId: string) => {
    pending.current.get(requestId)?.abort();
  }, []);
  const clientReady = useCallback(
    (ready: boolean) => {
      scope.current.clientReady = ready;
      void requestHttp({
        requestId: `presence_${newId()}`,
        method: 'POST',
        path: '/api/tim/presence'
      });
    },
    [requestHttp]
  );
  const syncSession = useCallback(() => {
    dispose();
    scope.current.clientReady = false;
    const value = current.current;
    value.send({
      type: 'SESSION',
      protocolVersion: 1,
      userId: value.available ? value.userId || undefined : undefined,
      role: value.role,
      platform: value.platform,
      runtime: value.runtime,
      lang: value.lang
    });
  }, [dispose]);
  useEffect(() => {
    if (options.iframeReady) syncSession();
    return dispose;
  }, [userId, available, options.iframeReady, syncSession, dispose]);
  // The IM schedules heartbeats; host visibility changes report immediately.
  useEffect(() => {
    if (!available || !options.iframeReady) return;
    const report = (hidden = false) => {
      scope.current.hidden = hidden;
      void requestHttp({
        requestId: `presence_${newId()}`,
        method: 'POST',
        path: '/api/tim/presence'
      });
    };
    const update = () => report();
    const hide = () => report(true);
    document.addEventListener('visibilitychange', update);
    window.addEventListener('pageshow', update);
    window.addEventListener('pagehide', hide);
    window.addEventListener('online', update);
    return () => {
      document.removeEventListener('visibilitychange', update);
      window.removeEventListener('pageshow', update);
      window.removeEventListener('pagehide', hide);
      window.removeEventListener('online', update);
    };
  }, [available, options.iframeReady, requestHttp]);
  const reportToastRender5sAck = useCallback(
    (items: TimUnreadMessageItem[]) => {
      const user = userId;
      if (
        user !== current.current.userId ||
        !available ||
        !scope.current.clientReady ||
        document.visibilityState !== 'visible'
      )
        return;
      const messages = items.flatMap((item) =>
        Number.isSafeInteger(item.message.sequence) &&
        Number(item.message.sequence) > 0
          ? [
              {
                timGroupId: item.timGroupId,
                msgSeq: Number(item.message.sequence)
              }
            ]
          : []
      );
      if (!messages.length) return;
      const body = {
        pageSessionId: scope.current.pageId,
        displayId: newId(),
        shownAt: Date.now(),
        pageVisible: true,
        items: messages
      };
      const run = async (attempt: number): Promise<void> => {
        if (
          !mounted.current ||
          current.current.userId !== user ||
          document.visibilityState !== 'visible' ||
          Date.now() - body.shownAt > 25_000
        )
          return;
        const result = await requestHttp(
          {
            requestId: `ack_${body.displayId}_${attempt}`,
            method: 'POST',
            path: '/api/tim/toast-render-5s-ack',
            body
          },
          'host'
        );
        const retryable =
          (result.body as { retryable?: boolean })?.retryable ||
          result.status === 408 ||
          result.status === 429 ||
          result.status >= 500;
        if (retryable && attempt < 3) {
          await new Promise((resolve) =>
            setTimeout(resolve, [1000, 3000, 5000][attempt])
          );
          await run(attempt + 1);
        }
      };
      void run(0);
    },
    [userId, available, requestHttp]
  );
  return {
    ready: available && options.iframeReady,
    request,
    cancel,
    clientReady,
    syncSession,
    reportToastRender5sAck
  };
}
